import React, { type SyntheticEvent, useState, useCallback } from 'react';
import { styled } from '@compiled/react';
import EditIcon from '@atlaskit/icon/core/edit';
import EditFilledIcon from '@atlaskit/icon/glyph/edit-filled';
import { token } from '@atlaskit/tokens';
import type { AnalyticsEvent } from '@atlassian/jira-common-analytics-v2-wrapped-components/src/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { ModalTriggerButton } from '@atlassian/jira-software-modal-dialog/src/ui/modal-trigger-button/index.tsx';
import type { SprintId } from '../../../model/types.tsx';
import EditSprintModalRelay from '../../modals/edit-sprint-modal-relay/index.tsx';
import messages from '../messages.tsx';

const ADD_SPRINT_DATES_BUTTON_ICON_SELECTOR = 'software-backlog-add-sprint-dates-button-icon';

const AddSprintDates = ({ cardListId }: { cardListId: SprintId }) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isEditSprintModalOpen, setIsEditSprintModalOpen] = useState(false);

	const onEditSprintClick = useCallback((event: SyntheticEvent, analyticsEvent: AnalyticsEvent) => {
		event.stopPropagation();
		fireUIAnalytics(analyticsEvent, 'openAddSprintDates');
		setIsEditSprintModalOpen(true);
	}, []);

	const onEditSprintModalCloseClick = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		fireUIAnalytics(analyticsEvent, 'closeAddSprintDates');
		setIsEditSprintModalOpen(false);
	}, [createAnalyticsEvent]);

	const editIcon = fg('tnk-1620-visual-refresh-software-backlog-icons') ? (
		<EditIcon
			label=""
			color={token('color.icon')}
			LEGACY_fallbackIcon={EditFilledIcon}
			LEGACY_size="small"
			LEGACY_primaryColor={token('color.icon', '#44546F')}
		/>
	) : (
		<EditFilledIcon label="" size="small" primaryColor={token('color.icon', '#44546F')} />
	);

	return (
		<>
			{isEditSprintModalOpen && (
				<EditSprintModalRelay
					cardListId={cardListId}
					onClose={onEditSprintModalCloseClick}
					onSubmit={onEditSprintModalCloseClick}
				/>
			)}
			<ButtonWrapper>
				<ModalTriggerButton appearance="subtle" spacing="compact" onClick={onEditSprintClick}>
					<ButtonContentContainer>
						<ButtonIcon data-component-selector={ADD_SPRINT_DATES_BUTTON_ICON_SELECTOR}>
							{editIcon}
						</ButtonIcon>
						<ButtonText>{formatMessage(messages.addSprintDates)}</ButtonText>
					</ButtonContentContainer>
				</ModalTriggerButton>
			</ButtonWrapper>
		</>
	);
};

export default AddSprintDates;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonText = styled.span({
	display: 'block',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonIcon = styled.span({
	display: 'block',
	height: '16px',
	marginRight: token('space.025', '2px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonContentContainer = styled.span({
	display: 'flex',
	alignItems: 'center',
	'@media (max-width: 768px)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		[`& > [data-component-selector="${ADD_SPRINT_DATES_BUTTON_ICON_SELECTOR}"]`]: {
			display: 'none',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	fontSize: token('space.150', '12px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		padding: `${token('space.0', '0px')} ${token('space.025', '2px')} ${token('space.0', '0px')} ${token('space.025', '2px')}`,
	},
});
